import React, { useContext, useState } from 'react';
import { CaptionWriterView, CaptionWritterViewProps } from './CaptionWriter.view';
import { PostEditorContext } from '../states/PostEditorProvider';
import { Network, PlanUsageType, UsageFieldsFragmentDoc } from '../../../models/graphql';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { useForm } from 'react-hook-form';
import { FormOptions } from '../../../constants/form';
import { FieldValues } from 'react-hook-form/dist/types';
import { UseFormProps } from 'react-hook-form/dist/types/form';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { showToast } from '../../../helpers/showToast';
import { CaptionWriterViewType, PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { Debugger } from '../../../components/containers/Debugger';
import { useDidMount } from '../../../hooks/useDidMount';
import { Link } from 'react-router-dom';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { GENERATING_CAPTIONS_PLACEHOLDER } from '../states/initial';
import { useApolloClient } from '@apollo/client';

const getNetwork = (profile: SelectedSocialProfileValue['__typename']): Network | null => {
  switch (profile) {
    case 'FacebookSocialProfile':
      return Network.FACEBOOK;
    case 'PinterestSocialProfile':
      return Network.PINTEREST;
    case 'InstagramSocialProfile':
      return Network.INSTAGRAM;
    case 'TikTokSocialProfile':
      return Network.TIKTOK;
    case 'GoogleSocialProfile':
      return Network.GOOGLE;
    case 'TwitterSocialProfile':
      return Network.TWITTER;
    case 'LinkedInSocialProfile':
      return Network.LINKEDIN;
    default:
      return null;
  }
};

export interface CaptionWriterFormValues extends FieldValues {
  propmptBase: string;
  prompt: string;
}

const isBasePromptItemsValueGuard = (value: FormElementValueType): value is string => {
  return typeof value === 'string';
};

export const CaptionWriterController = (): JSX.Element => {
  // Put All your methods and logic here
  const { selectedOrganization } = useSelectedOrganization();
  const { isOwner, limitAiCaption } = useOrganizationPlan();
  const organizationId = selectedOrganization?.id;
  const { state } = useContext(PostEditorContext);
  const [postGroupData, dispatchPostGroupData] = useContext(PostEditorGroupDataContext);
  const { aiCaptionWriter, postGroupIndex } = postGroupData;
  const { profiles, customizeContentPerProfile, activeCustomizeIndex, posts } = state.postGroups[postGroupIndex];
  const { captionResults, prompt, propmptBase, viewType, loading } = aiCaptionWriter;
  const CastedFormOptions = FormOptions as UseFormProps<CaptionWriterFormValues>;
  const aiFormInstance = useForm<CaptionWriterFormValues>({
    ...CastedFormOptions,
    defaultValues: { prompt, propmptBase }
  });
  const { cache } = useApolloClient();
  const [promptLabel, setPromptLabel] = useState<string>(aiCaptionWriter.promptLabel);
  const customizeIndex = customizeContentPerProfile ? activeCustomizeIndex : null;
  const selectedProfiles =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].customizedProfileId
        ? profiles.filter(p => p.profileId === posts[customizeIndex].customizedProfileId)
        : []
      : profiles;
  const mappedProfiles = selectedProfiles.flatMap(p => {
    const nwrk = getNetwork(p.__typename);
    return nwrk ?? [];
  });
  const networks = Array.from(new Set(mappedProfiles));

  useDidMount(() => () => {
    const data = aiCaptionWriter;
    const { propmptBase, prompt } = aiFormInstance.getValues();
    const viewType = data.loading ? CaptionWriterViewType.results : CaptionWriterViewType.initial;
    dispatchPostGroupData({ type: 'setAiPromptsAndViewType', payload: { viewType, prompt, propmptBase } });
  });

  const updateBasePrompt: FormElementAction = (v, complete) => {
    if (isBasePromptItemsValueGuard(v)) {
      aiFormInstance.setValue('prompt', v);
      aiFormInstance.setFocus('prompt');
    }
    setPromptLabel(v ? 'Edit your prompt' : 'Or write your own prompt');
    complete();
  };

  const submitHandler: CaptionWritterViewProps['submitHandler'] = async () => {
    // code goes here
    const isValid = await aiFormInstance.trigger();
    if (!isValid || !organizationId) return;
    const prompt = aiFormInstance.getValues('prompt');
    dispatchPostGroupData({
      type: 'initateCaptions',
      payload: {
        prompt,
        networks,
        organizationId,
        // this should not be done here, but in the effect
        onPlanLimitError: () => {
          dispatchPostGroupData({ type: 'setAiLimitReached', payload: true });
          if (captionResults === GENERATING_CAPTIONS_PLACEHOLDER) {
            dispatchPostGroupData({ type: 'setAiViewType', payload: CaptionWriterViewType.initial });
          } else {
            dispatchPostGroupData({ type: 'setAiResults', payload: captionResults });
            dispatchPostGroupData({ type: 'setAiViewType', payload: CaptionWriterViewType.results });
          }
          const oldUsage: PlanUsageType | null = cache.readFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}'
          });
          const newUsage = { ...oldUsage, aiCredits: limitAiCaption + 1 };
          cache.writeFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}',
            data: newUsage
          });
          showToast({
            severity: 'warn',
            summary: 'Upgrade your plan',
            sticky: true,
            detail: (
              <>
                {isOwner ? (
                  <span>
                    You have run out of AI Text credits for this month. Please{' '}
                    <Link to={'/settings/manage-subscription?src=plan_limit_reached&limit=ai_text'} target="_blank">
                      choose a higher plan, or customize your current one with add-ons.
                    </Link>
                  </span>
                ) : (
                  <span>
                    You have run out of AI Text credits for this month. Please contact your Organization Owner to
                    upgrade your plan.
                  </span>
                )}
              </>
            )
          });
        }
      }
    });
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(captionResults);
    showToast({
      severity: 'success',
      summary: 'Copied to Clipboard',
      detail: 'Your AI-generated caption can now be pasted anywhere you want.'
    });
  };

  const goBack = () => {
    dispatchPostGroupData({ type: 'setAiViewType', payload: CaptionWriterViewType.initial });
  };

  const goToResults = () => {
    dispatchPostGroupData({ type: 'setAiViewType', payload: CaptionWriterViewType.results });
  };

  const viewProps: CaptionWritterViewProps = {
    goBack,
    goToResults,
    networks,
    aiFormInstance,
    updateBasePrompt,
    submitHandler,
    captionResults,
    copyText,
    promptLabel,
    propmptBase,
    viewType,
    loading
  };
  return (
    <div className={'relative'}>
      <Debugger tooltipContent={'caption writted data'} modalContent={{ ...aiCaptionWriter, networks }} />
      <CaptionWriterView {...viewProps} />
    </div>
  );
};
